/**
 * 语言导航 Hook
 * 封装语言切换和路由导航逻辑
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../useTranslation';
import { buildLocalizedPath } from '../detectLanguage';
import type { ActiveLanguage } from '../types';

interface UseLanguageNavigationReturn {
  /** 当前语言 */
  currentLanguage: ActiveLanguage;
  /** 切换语言并导航 */
  changeLanguage: (newLang: ActiveLanguage, options?: { navigate?: boolean }) => void;
  /** 构建带语言前缀的路径 */
  buildPath: (path: string) => string;
  /** 从当前路径移除语言前缀 */
  getPathWithoutLang: (path?: string) => string;
}

/**
 * 语言导航 Hook
 * 处理语言切换时的状态更新和路由导航
 */
export function useLanguageNavigation(): UseLanguageNavigationReturn {
  const { language: currentLanguage, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * 从路径中提取语言前缀
   */
  const getPathWithoutLang = useCallback((path: string = location.pathname): string => {
    return path.replace(/^\/(en|zh-CN|ja)(?:\/|$)/, '/');
  }, [location.pathname]);

  /**
   * 构建带当前语言前缀的路径
   */
  const buildPath = useCallback((path: string): string => {
    return buildLocalizedPath(currentLanguage, path);
  }, [currentLanguage]);

  /**
   * 切换语言
   * @param newLang - 新语言代码
   * @param options - 配置选项
   * @param options.navigate - 是否自动导航到新语言路径，默认为 true
   */
  const changeLanguage = useCallback(
    (newLang: ActiveLanguage, options: { navigate?: boolean } = {}) => {
      const { navigate: shouldNavigate = true } = options;

      // 如果语言相同，不执行任何操作
      if (newLang === currentLanguage) {
        return;
      }

      // 更新语言状态
      setLanguage(newLang);

      // 导航到新语言路径
      if (shouldNavigate) {
        const currentPathWithoutLang = getPathWithoutLang();
        const newPath = buildLocalizedPath(newLang, currentPathWithoutLang);
        navigate(newPath, { replace: true });
      }
    },
    [currentLanguage, setLanguage, navigate, getPathWithoutLang]
  );

  return {
    currentLanguage,
    changeLanguage,
    buildPath,
    getPathWithoutLang,
  };
}
